import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import { assertNever } from 'owa-assert';
import { hasReadRights, isRecurringMaster } from 'owa-calendar-event-capabilities';
import { ClientItemId } from 'owa-client-ids';
import { EventsCacheLockId } from 'owa-events-cache';
import { FullItemInfo, FullItemLoadState } from '../../store/schema/FullItemsMruMap';
import { loadCalendarEvent } from '../../services/loadCalendarEvent';
import { logUsage } from 'owa-analytics';
import { MAX_RETRIES_TO_LOAD_CALENDAR_EVENT_FROM_SERVER } from '../../constants';
import { trace } from 'owa-trace';
import {
    getFullItemInfoFromMRU,
    getFullItemInfoForLockMRU,
} from '../../selectors/fullItemInfoSelectors';
import {
    getCalendarEventWithKey,
    getCalendarEventWithId,
} from '../../selectors/calendarFolderEventsSelectors';
import {
    fetchingFullCalendarEventForLock,
    fetchingFullCalendarEventForLockFailed,
    fullCalendarEventLoadedForLock,
    fullCalendarEventWasUsed,
} from '../../actions/eventsCacheActions';
import ItemId from 'owa-service/lib/contract/ItemId';

// The map of ongoing requests to server for fetching a full item.
// This map is maintained so if a request to fetch full item comes
// in while there is an ongoing request to server then we return the
// same promise instead of sending another request to the server.
const fetchingFullItemPromises: { [eventKey: string]: Promise<CalendarEvent> } = {};

function startedFetchingFullItem(eventKey: string, fetchPromise: Promise<CalendarEvent>) {
    fetchingFullItemPromises[eventKey] = fetchPromise;
}

function endedFetchingFullItem(eventKey: string) {
    delete fetchingFullItemPromises[eventKey];
}

function getPromiseForFetchingFullItem(eventKey: string) {
    return fetchingFullItemPromises[eventKey] || null;
}

export type GetExistingCalendarEvent = (itemId: ClientItemId) => CalendarEvent | null;

export type FetchFullCalendarEventResult = {
    event: CalendarEvent;
    fullEventPromise: Promise<CalendarEvent>;
} | null;

const enum FullEventPromiseSource {
    Server,
    ExistingPartialEvent,
    ExistingFullEvent,
}

interface FetchFullCalendarEventResultWithMetadata extends FetchFullCalendarEventResult {
    promiseSource: FullEventPromiseSource;
}

/**
 * Returns a full item from cache if available.
 * If not, returns a partial item and kicks off the retrieval to replace it with a full item
 *
 * @param lockId The lock id for which to fetch the full calendar event
 * @param eventId The Id of the item to retrieve
 * @param folderId The folder id of the item
 * @param getExistingCalendarEvent The function that returns calendar event if its already there
 * @param eventScope The scope of the event to fetch
 */
export function fetchFullCalendarEvent(
    lockId: EventsCacheLockId,
    eventId: ClientItemId,
    folderId: string | null = null,
    getExistingCalendarEvent: GetExistingCalendarEvent = () => null,
    eventScope:
        | EventScope.Default
        | EventScope.AllInstancesInSeries
        | EventScope.ThisInstanceOnly = EventScope.Default
): FetchFullCalendarEventResult {
    const getSeriesMaster = eventScope === EventScope.AllInstancesInSeries;
    const getCalendarEvent = createGetCalendarEventFunc(folderId, getExistingCalendarEvent);

    const fullItemInfo = getFullItemInfoFromMRU(eventId.Id);

    let returnVal: FetchFullCalendarEventResultWithMetadata;
    if (fullItemInfo) {
        // If a full item info is already present then that means
        // the full calendar event should also be present so we
        // try to get that
        folderId = folderId || fullItemInfo.folderId;
        returnVal = getFullCalendarEvent(
            folderId,
            eventId,
            fullItemInfo,
            eventScope,
            getCalendarEvent
        );
    } else {
        const event = getCalendarEvent(eventId);

        if (event && getSeriesMaster) {
            folderId = folderId || event.ParentFolderId.Id;
            returnVal = getSeriesMasterFullCalendarEvent(folderId, event);
        } else if (!event && getSeriesMaster) {
            // If we can't find existing event and we need to get series master
            // then as we can't figure out InstanceKey so we just return total null
            return null;
        } else {
            // If we dont have the partial event or if we can read then send a request to server
            // to get the full calendar event
            const promiseInfo = tryFetchFullCalendarEventFromServerIfCanReadEvent(
                event,
                eventId.Id /* eventKey */,
                eventId,
                eventScope,
                folderId || (event ? event.ParentFolderId.Id : null)
            );

            returnVal = {
                event,
                ...promiseInfo,
            };
        }
    }

    if (returnVal) {
        // If its getSeriesMaster then the returnVal.event should be there as
        // it would be the partial item
        const eventKey =
            getSeriesMaster ||
            (returnVal.event && isRecurringMaster(returnVal.event.CalendarItemType))
                ? returnVal.event.InstanceKey
                : eventId.Id;
        folderId = folderId || (returnVal.event ? returnVal.event.ParentFolderId.Id : null);

        returnVal = {
            ...returnVal,
            fullEventPromise: handleLoadingCalendarEvent(
                returnVal.fullEventPromise,
                returnVal.promiseSource,
                returnVal.event,
                lockId,
                folderId,
                eventKey,
                eventScope
            ),
        };
    }

    return returnVal;
}

function createGetCalendarEventFunc(
    folderId: string | null,
    getExistingCalendarEvent: GetExistingCalendarEvent
) {
    return (eventId: ClientItemId) => {
        const event: CalendarEvent = getCalendarEventWithId(eventId.Id, folderId);

        return event || (getExistingCalendarEvent && getExistingCalendarEvent(eventId));
    };
}

function getFullCalendarEvent(
    folderId: string,
    itemId: ClientItemId,
    fullItemInfo: FullItemInfo,
    eventScope: EventScope,
    getExistingCalendarEvent: GetExistingCalendarEvent
): FetchFullCalendarEventResultWithMetadata {
    const event: CalendarEvent = getExistingCalendarEvent(itemId);
    const getSeriesMaster = eventScope == EventScope.AllInstancesInSeries;

    if (event && getSeriesMaster) {
        // If we found the occurence event and we need to get series master
        // then we get series master
        return getSeriesMasterFullCalendarEvent(folderId, event);
    } else if (!event && getSeriesMaster) {
        // If we can't find existing event and we need to get series master
        // then as we can't figure out InstanceKey so we just return null
        return null;
    } else {
        switch (fullItemInfo.loadState) {
            case FullItemLoadState.Loaded:
                // As the full item is already loaded so return that directly
                return {
                    event,
                    fullEventPromise: Promise.resolve(event),
                    promiseSource: FullEventPromiseSource.ExistingFullEvent,
                };
            case FullItemLoadState.Loading:
                // The full item is currently loading so return the same promise
                return {
                    event,
                    fullEventPromise: getPromiseForFetchingFullItem(itemId.Id),
                    promiseSource: FullEventPromiseSource.Server,
                };
            case FullItemLoadState.Error:
                // There was error in previous request to fetch full item so fetch it again
                // If we dont have the partial event or if we can read then send a request to server
                // to get the full calendar event
                const promiseInfo = tryFetchFullCalendarEventFromServerIfCanReadEvent(
                    event,
                    itemId.Id /* eventKey */,
                    itemId,
                    eventScope,
                    folderId
                );

                return {
                    event,
                    ...promiseInfo,
                };
            default:
                return assertNever(fullItemInfo.loadState);
        }
    }
}

function getSeriesMasterFullCalendarEvent(
    folderId: string,
    existingEvent: CalendarEvent
): FetchFullCalendarEventResultWithMetadata {
    // Get the full item info for the series master based on instance key
    const eventKey = existingEvent.InstanceKey;
    const fullItemInfo = getFullItemInfoFromMRU(eventKey);

    if (fullItemInfo && fullItemInfo.loadState === FullItemLoadState.Loaded) {
        // As we know that full item is already loaded so get it directly from cache
        const event = getCalendarEventWithKey(eventKey, folderId);
        return {
            event: event,
            fullEventPromise: Promise.resolve(event),
            promiseSource: FullEventPromiseSource.ExistingFullEvent,
        };
    } else if (fullItemInfo && fullItemInfo.loadState === FullItemLoadState.Loading) {
        // The request to fetch full item is ongoing so we return the same promise
        const event = getCalendarEventWithKey(eventKey, folderId) || existingEvent;
        return {
            event: event,
            fullEventPromise: getPromiseForFetchingFullItem(eventKey),
            promiseSource: FullEventPromiseSource.Server,
        };
    } else {
        // Fetch the full calendar event for series master from server
        return {
            // As we are fetching a the series master we clone existing event and return so it later can be updated with full series master event.
            event: { ...existingEvent },
            fullEventPromise: fetchFullCalendarEventFromServer(
                eventKey,
                existingEvent.ItemId,
                EventScope.AllInstancesInSeries,
                existingEvent.ParentFolderId.Id,
                existingEvent.SeriesMasterItemId
            ),
            promiseSource: FullEventPromiseSource.Server,
        };
    }
}

function tryFetchFullCalendarEventFromServerIfCanReadEvent(
    event: CalendarEvent,
    eventKey: string,
    itemId: ClientItemId,
    eventScope: EventScope,
    folderId: string
): Pick<FetchFullCalendarEventResultWithMetadata, 'fullEventPromise' | 'promiseSource'> {
    return !event || hasReadRights(event)
        ? {
              fullEventPromise: fetchFullCalendarEventFromServer(
                  eventKey,
                  itemId,
                  eventScope,
                  folderId,
                  event && event.SeriesMasterItemId
              ),
              promiseSource: FullEventPromiseSource.Server,
          }
        : {
              fullEventPromise: Promise.resolve(event),
              promiseSource: FullEventPromiseSource.ExistingPartialEvent,
          };
}

async function handleLoadingCalendarEvent(
    loadCalendarEventPromise: Promise<CalendarEvent>,
    loadCalendarEventPromiseSource: FullEventPromiseSource,
    existingEvent: CalendarEvent | null,
    lockId: EventsCacheLockId,
    folderId: string | null,
    eventKey: string,
    eventScope: EventScope
): Promise<CalendarEvent> {
    let fullItemInfo = getFullItemInfoForLockMRU(lockId, eventKey);
    let calendarEvent;
    try {
        trace.info(
            `[fetchFullCalendarEvent] Handling loading full calendar event Key: ${eventKey}, Lock: ${lockId}`
        );

        if (folderId && (!fullItemInfo || fullItemInfo.loadState !== FullItemLoadState.Loaded)) {
            // We only call the fetching full calendar event if the full item is not present,
            // if its not already loaded or if it was not already locked by the current lock
            trace.info(
                `[fetchFullCalendarEvent] Loading full calendar event Key: ${eventKey}, Lock: ${lockId}`
            );
            fetchingFullCalendarEventForLock(lockId, folderId, eventKey, existingEvent);
        }

        // if the fullEvent is already present in the MRU but not for this lock, then we will be trying to fetch again from a resolved promise.
        // However in that case, we do not want to await on the promise, as awaiting on it can lead to race conditions where another fetch call comes for the same event
        // and even before the await is finished, it tries to look up for that event when its in loading state and does not find any pending promise.
        if (loadCalendarEventPromiseSource == FullEventPromiseSource.ExistingFullEvent) {
            calendarEvent = existingEvent;
        } else {
            calendarEvent = await loadCalendarEventPromise;
        }

        if (!calendarEvent) {
            // A null event returned from loadCalendarEvent represents that fetching full calendar
            // event failed so we throw error here. It will be handled properly (in the catch section) by marking
            // the full item info as failed so that next time a request for fetchFullCalendarEvent comes
            // it will fetch from server. The promise returned back to the caller will also throw so it can be handled
            // properly by client.
            throw new Error(
                `[fetchFullCalendarEvent] Unable to fetch full calendar event for event Key: ${eventKey}`
            );
        }

        const calendarEventFolderId = folderId || calendarEvent.ParentFolderId.Id;

        // Get the full item info again as the previous fetched `fullItemInfo` might
        // be invalid because it was before the await so it might have
        // changed by now
        const prevFullItemInfo = fullItemInfo;
        fullItemInfo = getFullItemInfoForLockMRU(lockId, eventKey);

        if (
            calendarEventFolderId &&
            (!fullItemInfo || fullItemInfo.loadState !== FullItemLoadState.Loaded)
        ) {
            trace.info(
                `[fetchFullCalendarEvent] Loaded full calendar event Key: "${eventKey}", Lock: "${lockId}", FolderId: "${calendarEventFolderId}"`
            );

            logUsage('FullCalendarEventLoaded', {
                eventKey,
                lockId,
                prevFullItemLoadState: prevFullItemInfo ? prevFullItemInfo.loadState : null,
                fullItemLoadState: fullItemInfo ? fullItemInfo.loadState : null,
                isFullItem: calendarEvent.RequiredAttendees !== undefined,
                isSameAsExistingEvent: existingEvent === calendarEvent,
            });

            // As the event can be a full event returned from server we need to force override
            // all the properties returned from the server even though they might be undefined so that
            // no properties from the partial event lingers e.g. in case of master full item we use
            // the instance item as partial and we don't want any of its properties to be part of
            // master item when it gets loaded.
            const forceOverrideAllPropertiesForEventIfAvailable =
                loadCalendarEventPromiseSource === FullEventPromiseSource.Server;

            fullCalendarEventLoadedForLock(
                lockId,
                calendarEventFolderId,
                eventKey,
                calendarEvent,
                eventScope,
                forceOverrideAllPropertiesForEventIfAvailable
            );
        } else {
            trace.info(
                `[fetchFullCalendarEvent] Full calendar event already loaded. EventKey: "${eventKey}", Lock: "${lockId}", FolderId: "${calendarEventFolderId}"`
            );
            fullCalendarEventWasUsed(lockId, eventKey);
        }

        return getCalendarEventWithKey(eventKey, folderId);
    } catch (error) {
        trace.warn(
            `[fetchFullCalendarEvent] Received error when fetching for ${eventKey} with error ${error}`
        );

        const prevFullItemInfo = fullItemInfo;
        fullItemInfo = getFullItemInfoForLockMRU(lockId, eventKey);
        logUsage('FullCalendarEventLoaded', {
            eventKey,
            lockId,
            prevFullItemLoadState: prevFullItemInfo ? prevFullItemInfo.loadState : null,
            fullItemLoadState: fullItemInfo ? fullItemInfo.loadState : null,
            isFullItem: false,
            isSameAsExistingEvent: false,
        });

        if (folderId) {
            fetchingFullCalendarEventForLockFailed(lockId, folderId, eventKey);
        }

        throw error;
    }
}

function fetchFullCalendarEventFromServer(
    eventKey: string,
    itemId: ClientItemId,
    eventScope: EventScope,
    parentFolderId: string,
    seriesMasterId: ItemId
) {
    const fullEventPromise = (async () => {
        try {
            return await loadCalendarEventWithRetries(
                MAX_RETRIES_TO_LOAD_CALENDAR_EVENT_FROM_SERVER,
                itemId,
                eventScope,
                parentFolderId,
                seriesMasterId
            );
        } finally {
            endedFetchingFullItem(eventKey);
        }
    })();

    startedFetchingFullItem(eventKey, fullEventPromise);

    return fullEventPromise;
}

async function loadCalendarEventWithRetries(
    maxRetries: number,
    itemId: ClientItemId,
    eventScope: EventScope,
    parentFolderId: string,
    seriesMasterId: ItemId
): Promise<CalendarEvent> {
    let currentTry = 0;

    while (currentTry < maxRetries) {
        try {
            const response = await loadCalendarEvent(
                itemId,
                parentFolderId,
                eventScope,
                seriesMasterId
            );
            currentTry++;
            if (response || currentTry >= maxRetries) {
                // If there is some response returned then return that immediately
                // or if we have exceeded the retries then just return
                return response;
            }
        } catch (error) {
            trace.warn(
                `[fetchFullCalendarEvent] Received error when trying to load calendar event from server for "${
                    itemId.Id
                }" with try ${currentTry}: Error ${error.toString()}`
            );

            currentTry++;

            if (currentTry >= maxRetries) {
                // If we have exceeded the retries then re-throw the error
                throw error;
            }
        }
    }

    // We should not reach here, we should always return before this.
    return null;
}
